<template>
  <s-loader v-if="loading" class="general-vision-item__chart"/>
  <div v-else>
    <e-chart
      class="general-vision-item__chart"
      v-if="hasChartData"
      autoresize
      :ref="chartName"
      @click="onSelectHandler"
    />
    <div
      v-else
      class="general-vision-item__empty-chart"
    >
      <i
        aria-hidden="true"
        :class="icon"
      />
      <div class="general-vision-item__empty-text-wrapper">
        <p>{{$t('dashboard.no-alarms')}}</p>
        <p
          class="general-vision-item__empty-text-title"
          :style="{ 'color': color}"
        >{{title}}</p>
        <p>{{$t('dashboard.acted-in-period')}}</p>
      </div>
    </div>
  </div>
</template>
<script>
import themeService from '@/services/themeService';
import { AlarmStatusFilter } from 'sigma-mappings';

const colors = {
  actedAndNotRecognized: themeService.getStyleVar('color-acted-and-not-recognized'),
  actedAndRecognized: themeService.getStyleVar('color-acted-and-recognized'),
  notActedAndNotRecognized: themeService.getStyleVar('color-not-acted-and-not-recognized'),
};

export default {
  name: 'AlarmGeneralDonutVisionChart',
  props: {
    title: {
      type: String,
      required: true,
    },
    chartData: {
      type: Array,
      required: true,
    },
    color: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      required: true,
    },
    chartName: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
    },
  },
  computed: {
    hasChartData() {
      return (this.chartData || []).length > 0;
    },
    chartTitle() {
      const alarmCount = (this.chartData || []).reduce((sum, item) => sum + item.value, 0);
      return `${alarmCount} ${this.title}`;
    },
    legendData() {
      const legendData = this.chartData.map(item => ({
        name: item.name,
        textStyle: this.getColorByStatus(item.status),
      }));

      return legendData;
    },
  },
  mounted() {
    this.setChartData();
  },
  methods: {
    onSelectHandler(params) {
      const selected = {
        classifications: [params.data.classification],
        alarmStatus: [params.data.status],
      };
      this.$emit('onGraphClicked', selected);
    },
    setChartData() {
      if (!this.hasChartData) {
        return;
      }

      this.$refs[this.chartName].delegateMethod(
          'setOption',
          this.getOptions(),
          { replaceMerge: ['title', 'legend', 'tooltip', 'series'] },
        );
    },
    getColorByStatus(status) {
        switch (status) {
          case AlarmStatusFilter.ACTED_NOT_RECOGNIZED:
            return {
              color: colors.actedAndNotRecognized,
            };
          case AlarmStatusFilter.ACTED_RECOGNIZED:
            return {
              color: colors.actedAndRecognized,
            };
          case AlarmStatusFilter.NOT_ACTED_NOT_RECOGNIZED:
            return {
              color: colors.notActedAndNotRecognized,
            };
          default:
            return {};
        }
    },
    getTooltip({ data, value }) {
      return `${this.$t(`enums.commons.classification-type.${data.classification}`)}\n`
        + '<br>\n'
        + `${this.$t(`enums.alarms.${data.status}`)}: ${this.$n(value)} (${this.$n(data.percent)}%)`;
    },
    getOptions() {
      return {
        backgroundColor: 'transparent',
        title: {
          top: '30px',
          left: '30%',
          text: (this.chartTitle || '').toUpperCase(),
          textStyle: {
            color: this.color,
          },
        },
        tooltip: {
          trigger: 'item',
          confine: true,
          backgroundColor: themeService.getStyleVar('color-background-tooltip'),
          textStyle: {
            color: 'white',
          },
          formatter: this.getTooltip,
        },
        color: [
          colors.actedAndNotRecognized,
          colors.actedAndRecognized,
          colors.notActedAndNotRecognized,
        ],
        legend: {
          data: this.legendData,
          top: '60px',
          left: '28%',
          orient: 'vertical',
          icon: 'circle',
          textStyle: {
            fontWeight: 'bold',
            fontSize: '0.71rem',
          },
          itemWidth: 6,
          itemHeight: 6,
        },
        grid: {
          top: 20,
          bottom: 20,
          right: 20,
          left: 20,
        },
        series: {
          type: 'pie',
          radius: [30, 50],
          right: '72%',
          itemStyle: {
            borderWidth: 0,
          },
          label: {
            show: false,
            position: 'center',
          },
          labelLine: {
            show: false,
          },
          data: this.chartData,
        },
      };
    },
  },
};
</script>
<style scoped lang="sass">
@import "src/styles/defaults/mobile-first"

.general-vision-item
  &__chart
    height: 150px
    width: 400px
  &__empty-chart
    display: flex
    align-items: center
    height: 150px
    width: 350px
    +media-max-sm()
      justify-content: center
  &__empty-text
    &-wrapper
      display: flex
      flex-direction: column
      justify-content: center
      align-items: center
      margin-left: 1rem
      & p
        margin-bottom: 0
        text-transform: uppercase
        font-weight: bolder
        &:not(.general-vision-item__empty-text-title)
          font-size: 0.71rem
    &-title
      font-size: 1.71rem
</style>
